import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import { Switch } from 'pretty-checkbox-react';
import Select from 'react-select';
import api from '../../api';
/* actions */
import { getAllDepartments } from '../../redux/actions/departments';
import { getAllCompanies } from '../../redux/actions/companies';
import { getAllPermissionsGroup } from '../../redux/actions/permissionsGroup';
import { editUsers } from '../../redux/actions/users';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
import MultiSelect from '../../components/MultiSelect';
/* form-validation */
import schema from '../../form-validation/user';
/* utils */
import {
  handleMultiSelect,
  handleMultiDeselect
} from '../../utils/multiSelect';
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption,
  handleChangeSelect
} from '../../utils/select';
import sortable from '../../utils/sortable';
import handleChangeDefault from '../../utils/handleChangeDefault';
import { idCompany4Case } from '../../utils/defaultPermissions';

function Edit (props) {
  const { id } = props.match.params;
  const INITIAL_VALUES = {
    name: '',
    email: '',
    username: '',
    nameExtension: '',
    department: '',
    type: '',
    departments: [],
    logged: false,
    company: '',
    permissionsGroup: ''
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, setError, setValue } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const sessionUser = useSelector(state => state.authentications.user);
  const company = sessionUser.company._id;
  const users = useSelector(state => state.users);
  const user = users.data.find(usr => usr._id === id);
  const departments = useSelector(state => state.departments.data);
  const [data, setData] = useState(INITIAL_VALUES);
  const [selectedOptions, setSelectedOptions] = useState({ departments: [] });
  const [dpts, setDpts] = useState([]);
  const [changePassword, setChangePassword] = useState(false);
  const companies = useSelector(state => state.companies.data);
  const [companyOptions, setCompanyOptions] = useState([firstOption]);
  const permissionsGroup = useSelector(state => state.permissionsGroup.data);
  const [permissionsGroupOptions, setPermissionsGroupOptions] = useState([
    firstOption
  ]);

  useEffect(() => {
    dispatch(getAllDepartments());
    dispatch(getAllPermissionsGroup());
    dispatch(
      getAllCompanies(idCompany4Case === company ? {} : { _id: company })
    );
  }, [dispatch]);

  useEffect(() => {
    if (departments.length) {
      const dpts = departments
        .map(department => ({
          value: department._id,
          text: department.name,
          sortValue: department.name
        }))
        .sort(sortable);
      setDpts([...dpts]);
    }
  }, [departments, setDpts]);

  useEffect(() => {
    (async function () {
      try {
        const params = { _id: id };
        const { data } = await api().get('/api/users', { params });
        if (data[0]) {
          const newData = {
            ...INITIAL_VALUES,
            ...data[0],
            department: data[0].department ? data[0].department._id : '',
            company: data[0].company ? data[0].company._id : '',
            permissionsGroup: data[0].permissionsGroup
              ? data[0].permissionsGroup._id
              : ''
          };
          delete newData.password;
          delete newData.confirmPassword;
          setData(newData);
        }
      } catch (err) {
        console.error('Error: ', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (user) {
      const departments = dpts.filter(department =>
        user.departments.includes(department.value)
      );
      setSelectedOptions({ departments });
    }
  }, [user, dpts, setSelectedOptions]);

  useEffect(() => {
    if (companies.length) {
      const options = companies
        .map(company => ({
          value: company._id,
          label: `${company.name}`,
          sortValue: `${company.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setCompanyOptions([...options]);
    }
  }, [companies, setCompanyOptions]);

  useEffect(() => {
    if (permissionsGroup.length) {
      const options = permissionsGroup
        .map(permissionsGrp => ({
          value: permissionsGrp._id,
          label: `${permissionsGrp.name}`,
          company: `${permissionsGrp.company._id}`,
          sortValue: `${permissionsGrp.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setPermissionsGroupOptions([...options]);
    }
  }, [permissionsGroup, setPermissionsGroupOptions]);

  const submit = () => {
    dispatch(editUsers(data, submitButton.current));
  };

  const handleChangeCheckOption = () => {
    if (changePassword) {
      setError('password');
      setError('confirmPassword');
      const newData = { ...data };
      delete newData.password;
      delete newData.confirmPassword;
      setData({ ...newData });
    }
    setChangePassword(!changePassword);
  };

  return (
    <div className="container-fluid">
      {users.editSuccess && (
        <AlertMsg type="success" msg="Usuário atualizado com sucesso" />
      )}
      {users.errorContent &&
        !users.errorContent.message.includes('400') &&
        users.editError && (
        <AlertMsg type="danger" msg="Falha durante atualização do usuário" />
      )}
      {users.errorContent &&
        users.errorContent.message.includes('400') &&
        users.editError && (
        <AlertMsg
          type="danger"
          msg="O nome de usuário informado já está em uso"
        />
      )}
      {!users.editSuccess && !users.editError && (
        <AlertMsg type="info" msg="Área atualização de usuários" />
      )}
      <FormHeader title="Atualizar Usuário" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label>
                Nome<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                autoComplete="off"
                onChange={evt => handleChangeDefault(evt, data, setData)}
                value={data.name}
                ref={register}
              />
              {errors.name && errors.name.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="email">
                E-mail<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control text-lowercase"
                id="email"
                name="email"
                autoComplete="off"
                onChange={evt => handleChangeDefault(evt, data, setData)}
                value={data.email}
                ref={register}
              />
              {errors.email && <ErrorMsgField type="email" />}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="username">
                Usuário<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control text-lowercase"
                id="username"
                name="username"
                autoComplete="off"
                onChange={evt => handleChangeDefault(evt, data, setData)}
                value={data.username}
                ref={register}
              />
              {errors.username && errors.username.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.username && errors.username.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
              {errors.username && errors.username.type === 'max' && (
                <ErrorMsgField type="max" />
              )}
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <label htmlFor="changePassword">
                Alterar Senha<span className="field-required">*</span>
              </label>
              <div className="checkbox">
                <Switch
                  type="checkbox"
                  color="success"
                  name="changePassword"
                  onChange={handleChangeCheckOption}
                ></Switch>
              </div>
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="password">
                Senha<span className="field-required">*</span>
              </label>
              <input
                type="password"
                className="form-control"
                id="password"
                name="password"
                autoComplete="new-password"
                onChange={evt => handleChangeDefault(evt, data, setData)}
                disabled={!changePassword}
                value={data.password || ''}
                ref={register}
              />
              {errors.password && errors.password.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.password && errors.password.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
              {errors.password && errors.password.type === 'max' && (
                <ErrorMsgField type="max" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="confirmPassword">
                Confirmar Senha<span className="field-required">*</span>
              </label>
              <input
                type="password"
                className="form-control"
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="off"
                onChange={evt => handleChangeDefault(evt, data, setData)}
                disabled={!changePassword}
                value={data.confirmPassword || ''}
                ref={register}
              />
              {errors.confirmPassword &&
                errors.confirmPassword.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.confirmPassword &&
                errors.confirmPassword.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
              {errors.confirmPassword &&
                errors.confirmPassword.type === 'max' && (
                <ErrorMsgField type="max" />
              )}
              {errors.confirmPassword &&
                errors.confirmPassword.type === 'confirmPassword' && (
                <ErrorMsgField type="confirmPassword" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="nameExtension">
                Nome do Ramal<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="nameExtension"
                name="nameExtension"
                autoComplete="off"
                onChange={evt => handleChangeDefault(evt, data, setData)}
                value={data.nameExtension}
                ref={register}
              />
              {errors.nameExtension &&
                errors.nameExtension.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="department">
                Departamento<span className="field-required">*</span>
              </label>
              <select
                className="form-control"
                id="department"
                name="department"
                onChange={evt => handleChangeDefault(evt, data, setData)}
                value={data.department}
                ref={register}
              >
                <option value="">-- Selecione --</option>
                {departments.map((dpt, i) => {
                  const _id = dpt._id || Date.now() + '' + i;
                  return (
                    <option key={_id} value={_id}>
                      {dpt.name}
                    </option>
                  );
                })}
              </select>
              {errors.department && errors.department.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="type">
                Perfil<span className="field-required">*</span>
              </label>
              <select
                className="form-control"
                id="type"
                name="type"
                onChange={evt => handleChangeDefault(evt, data, setData)}
                value={data.type}
                ref={register}
              >
                <option value="">-- Selecione --</option>
                {sessionUser.type === 'M' && <option value="M">Gestor</option>}
                <option value="S">Supervisor</option>
                <option value="O">Operador</option>
              </select>
              {errors.type && errors.type.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <div className="form-group form-inline mb-0">
                <label
                  className="col-sm-12 d-inline p-0 mb-2"
                  htmlFor="company"
                >
                  Empresa
                  <span className="field-required">*</span>
                </label>
                <Select
                  className="col p-0"
                  name="company"
                  ref={register}
                  onChange={evt => {
                    const { value } = evt;
                    const name = 'company';
                    if (data[name] !== value) {
                      setData({
                        ...data,
                        [name]: value,
                        permissionsGroup: ''
                      });
                      value
                        ? setError(name)
                        : setError(name, 'required', 'field is required');
                      setValue({ [name]: value });
                    }
                  }}
                  value={companyOptions.find(
                    company => company.value === data.company
                  )}
                  options={companyOptions}
                  isSearchable={true}
                  theme={customThemes}
                  styles={customStyle}
                  filterOption={customFilter}
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                />
              </div>
              {errors.company && errors.company.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <div className="form-group form-inline mb-0">
                <label
                  className="col-sm-12 d-inline p-0 mb-2"
                  htmlFor="permissionsGroup"
                >
                  Grupo de Permissões
                  <span className="field-required">*</span>
                </label>
                <Select
                  className="col p-0"
                  name="permissionsGroup"
                  ref={register}
                  onChange={evt =>
                    handleChangeSelect(
                      { ...evt, name: 'permissionsGroup' },
                      data,
                      setData,
                      setError,
                      setValue
                    )
                  }
                  value={permissionsGroupOptions.find(
                    permissionsGroup =>
                      permissionsGroup.value === data.permissionsGroup
                  )}
                  options={[firstOption].concat(
                    permissionsGroupOptions.filter(
                      option => option.company === data.company
                    )
                  )}
                  isSearchable={true}
                  theme={customThemes}
                  styles={customStyle}
                  filterOption={customFilter}
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                  isDisabled={!data.company}
                />
              </div>
              {errors.permissionsGroup &&
                errors.permissionsGroup.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <MultiSelect
          avaliableOptions={dpts}
          selectedOptions={selectedOptions.departments}
          handleMultiDeselect={options =>
            handleMultiDeselect(
              options,
              'departments',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          handleMultiSelect={options =>
            handleMultiSelect(
              options,
              'departments',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          valueProp="value"
          textProp="text"
          title="Departamentos Adicionais"
          name="departments"
          ref={register}
          error={!!errors.departments}
        />
        <FormFooter url="/users" reference={submitButton} />
      </form>
    </div>
  );
}

Edit.propTypes = {
  match: PropTypes.object
};

export default Edit;
